import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import clsx from 'clsx';

import { AutoNumeric } from '../../../../common/auto-numeric/AutoNumeric';
import { CommonProps } from './pricing-list/PricingItem';
import { calculateValue } from '../../../../../utils/helper-functions';
import { PricingCalculator } from '../../../../../utils/PricingCalculator';
import { useUserPermissions } from '../../../../../context/user/PermissionsProvider';
import { PoFormLabel, pricingConfig } from '../po-form-config';
import { FormPricingKeys } from './pricing-list/FormPricing';

import formStyles from '../PurchaseOrderForm.module.scss';
import { useAutoPricingState } from './context/AutoPricingProvider';
import { initialAutoPricingState } from '../../../../../constants/constants';

const { min } = pricingConfig[FormPricingKeys.SemiMonthlyRent].validations;

export const semiMonthlyRentTestId = 'SemiMonthlyRentComponent';

export const SemiMonthlyRent = (props: CommonProps) => {
  const { index, field, state } = props;
  const { hasAutoPricing } = useUserPermissions();
  const { autoPricings } = useAutoPricingState();
  const [value, setValue] = useState('');
  const { semiMonthlyRate: apiValue, weeklyRate, weeklyTerm } =
    autoPricings[state] || initialAutoPricingState;
  const [isDefaultFromPO, setIsDefaultFromPO] = useState(
    field.semiMonthlyRent !== ''
  );

  const weeklyTermWatched = useWatch({ name: `pricing[${index}].weeklyTerm` });
  const weeklyRentWatched = useWatch({ name: `pricing[${index}].weeklyRent` });

  const monthlyRent = () =>
    weeklyRentWatched &&
    calculateValue(
      PricingCalculator.monthlyRent,
      weeklyRentWatched,
      `${PricingCalculator.monthlyMultiplier()}`
    );

  const semiMonthlyRent = () =>
    monthlyRent &&
    calculateValue(PricingCalculator.semiMonthlyRent, monthlyRent());

  const fieldName = `pricing[${index}].semiMonthlyRent`;

  useEffect(() => {
    if (
      weeklyTerm === null &&
      weeklyRate === null &&
      apiValue === null &&
      !!weeklyTermWatched &&
      !!weeklyRentWatched &&
      !isDefaultFromPO
    ) {
      //* Value to be calculated from input change
      const rent =
        !!weeklyTermWatched && !!weeklyRentWatched && semiMonthlyRent() >= '1'
          ? semiMonthlyRent()
          : '';
      setValue(rent);
    } else {
      setValue('');
      setIsDefaultFromPO(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weeklyTermWatched, weeklyRentWatched]);

  const imperativeValueCheck =
    !!value ||
    !!apiValue ||
    weeklyRentWatched === '' ||
    weeklyTermWatched === '';

  return useMemo(
    () => (
      <div data-testid={semiMonthlyRentTestId}>
        <label htmlFor={fieldName}>{PoFormLabel.SemiMonthlyRate}:</label>
        <div className={clsx(formStyles.iconField, formStyles.noFillField)}>
          <span>$</span>
          <Controller
            as={AutoNumeric}
            name={fieldName}
            defaultValue={field.semiMonthlyRent}
            {...(imperativeValueCheck && {
              imperativeValue:
                value !== '' ? value : apiValue ? apiValue.toString() : '',
              imperativeMode: true
            })}
            options={{
              minimumValue: 0,
              decimalPlaces: 2,
              ...(!hasAutoPricing && {
                maximumValue: Number.MAX_SAFE_INTEGER
              })
            }}
            placeholder="--"
            disabled
            rules={{ required: true, min }}
          />
        </div>
      </div>
    ),
    [
      fieldName,
      field.semiMonthlyRent,
      imperativeValueCheck,
      value,
      apiValue,
      hasAutoPricing
    ]
  );
};
